import { APP_INITIALIZER, isDevMode, ModuleWithProviders, NgModule } from "@angular/core";
import { FormsModule, ReactiveFormsModule } from "@angular/forms";
import { BrowserModule } from "@angular/platform-browser";
import { ActivatedRouteSnapshot, RouteReuseStrategy, RouterModule } from "@angular/router";
import { ServiceWorkerModule } from "@angular/service-worker";
import { Capacitor } from "@capacitor/core";
import { AnimationController, IonicModule, IonicRouteStrategy } from "@ionic/angular";
import { IonicStorageModule } from "@ionic/storage-angular";
import { CookieService } from "ngx-cookie-service";

import { AppComponent } from "./app.component";
import { AppRoutingModule } from "./app-routing.module";
import { AccountService } from "./lib/account.service";
import { ConfigService } from "./lib/config.service";
import { API_URLS, setApiUrl } from "./lib/env";
import { NavigationService } from "./lib/navigation.service";
import { SurveyService } from "./lib/survey.service";
import { FormService } from "./lib/form.service";
import { QuestionService } from "./lib/question.service";
import { provideHttpClient } from "@angular/common/http";
import { DbConfigService } from "./lib/dbconfig.service";

function initializeApp(configService: ConfigService): () => Promise<void> {
  if (Capacitor.getPlatform() == "web") {
    setApiUrl(API_URLS[0]);
  } else if (Capacitor.getPlatform() == "ios") {
    setApiUrl(API_URLS[2]);
  } else if (Capacitor.getPlatform() == "android") {
    setApiUrl(API_URLS[1]);
  }

  return async (): Promise<void> => {
    await configService.init();
  };
}

function useReducedMotion(): boolean {
  return (
    window.matchMedia("(prefers-reduced-motion)").matches || window.sessionStorage.getItem("forceNoMotion") === "true"
  );
}

function getIonicModule(): ModuleWithProviders<IonicModule> {
  if (useReducedMotion()) {
    console.log("Reduced motion mode!", window.matchMedia("(prefers-reduced-motion)"));
    return IonicModule.forRoot({
      animated: false,
      navAnimation: (_) => new AnimationController().create(),
      rippleEffect: false,
      sanitizerEnabled: false,
    });
  } else {
    return IonicModule.forRoot({
      rippleEffect: false,
      sanitizerEnabled: false,
    });
  }
}

class ReloadRouteStrategy extends IonicRouteStrategy {
  override shouldReuseRoute(_future: ActivatedRouteSnapshot, _curr: ActivatedRouteSnapshot): boolean {
    return false;
  }
}

@NgModule({
  declarations: [AppComponent],
  imports: [
    BrowserModule,
    getIonicModule(),
    AppRoutingModule,
    IonicStorageModule.forRoot(),
    FormsModule,
    ReactiveFormsModule,
    RouterModule,
    ServiceWorkerModule.register("ngsw-worker.js", {
      enabled: !isDevMode(),
      // Register the ServiceWorker as soon as the application is stable
      // or after 30 seconds (whichever comes first).
      registrationStrategy: "registerWhenStable:30000",
    }),
  ],
  providers: [
    { provide: RouteReuseStrategy, useClass: ReloadRouteStrategy },
    { provide: Window, useValue: window },
    {
      provide: APP_INITIALIZER,
      useFactory: initializeApp,
      deps: [ConfigService],
      multi: true,
    },
    CookieService,
    AccountService,
    SurveyService,
    NavigationService,
    ConfigService,
    FormService,
    QuestionService,
    DbConfigService,
    provideHttpClient()
  ],
  bootstrap: [AppComponent],
})
export class AppModule { }
